import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, share, tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class SVGCacheService {
    private static cache: Map<string, SVGElement>;
    private static inProgressReqs: Map<string, Observable<SVGElement>>;
    private renderer: Renderer2;

    constructor(
        private httpClient: HttpClient,
        private rendererFactory: RendererFactory2,
    ) {
        this.renderer = this.rendererFactory.createRenderer(null, null);
        if (!SVGCacheService.cache) {
            SVGCacheService.cache = new Map<string, SVGElement>();
        }
        if (!SVGCacheService.inProgressReqs) {
            SVGCacheService.inProgressReqs = new Map<string, Observable<SVGElement>>();
        }
    }

    getSVG(url: string): Observable<SVGElement> {
        if (SVGCacheService.cache.has(url)) {
            return of(this.cloneSVG(SVGCacheService.cache.get(url) as SVGElement));
        }

        if (SVGCacheService.inProgressReqs.has(url)) {
            return SVGCacheService.inProgressReqs.get(url) as Observable<SVGElement>;
        }

        const req = this.httpClient.get(url, { responseType: 'text' }).pipe(
            tap(() => {
                SVGCacheService.inProgressReqs.delete(url);
            }),
            catchError((error: HttpErrorResponse) => {
                SVGCacheService.inProgressReqs.delete(url);
                return throwError(error.message);
            }),
            share(),
            map((svgText: string) => {
                const svgEl = this.svgElementFromString(svgText);
                SVGCacheService.cache.set(url, svgEl);
                return this.cloneSVG(svgEl);
            }),
        );

        SVGCacheService.inProgressReqs.set(url, req);

        return req;
    }

    private cloneSVG(svg: SVGElement): SVGElement {
        return svg.cloneNode(true) as SVGElement;
    }

    private svgElementFromString(str: string): SVGElement | never {
        const div = this.renderer.createElement('DIV');
        div.innerHTML = str;

        const svg = div.querySelector('svg') as SVGElement;

        if (!svg) {
            throw new Error('No SVG found in loaded contents');
        }

        return svg;
    }
}
